import React, { useState, useEffect } from "react";
import { Typography, Container, Box } from "@mui/material";
import api from "../../globals/axios";
import CustomAppBar from "../../components/AppBar";
import { useTranslation } from "react-i18next";
import LoaderContainer from "../../components/LoaderContainer";
import CustomButton from "../../components/Custom/CustomButton";
import Calendar from "../../assets/Icon/Calendar.png";
import CustomProgramCard from "../../components/Custom/CustomProgramCard";
import { useNavigate } from "react-router-dom";
import ConfirmationPopup from "../../components/Popup/GenericPopup";
import { useAlert } from "../../components/Alert";

export default function AllPrograms() {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [data, setData] = useState([]);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  useEffect(() => {
    loadPrograms();
  }, []);

  const loadPrograms = async () => {
    try {
      setLoading(true);
      const result = await api.get(`/programs/programs/`);
      setData(result.data);
    } catch (err) {
      alert(
        "Error: " +
          (err.response?.data?.non_field_errors?.[0] || "An error occurred")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAddProgram = () => {
    navigate("/pop-manager/create-program");
  };

  const handleViewProgramDetails = (programId) => {
    navigate(`/program/${programId}`);
  };

  const handleEditProgram = (programId) => {
    navigate(`/pop-manager/create-program/${programId}`);
  };

  const handleDeleteProgram = (programId) => {
    setSelectedProgramId(programId);
    setDeleteConfirmationOpen(true);
  };

  const handleOpenView = (programId) => {
    navigate(`/program/${programId}`);
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/programs/programs/${selectedProgramId}`);
      showAlert("success", "Program Deleted Successfully");
      loadPrograms();
    } catch (error) {
      console.error("Error deleting program:", error);
      showAlert("error", "Error deleting the Program");
    }
    setDeleteConfirmationOpen(false);
  };

  const renderProgramData = (program) => {
    const programInput = program.program_inputs?.[0] || {};
    return [
      {
        label: t("Program Name"),
        value: program.name || t("N/A"),
        type: "text",
      },
      {
        label: t("Crops"),
        value: programInput.crop || t("N/A"),
        type: "text",
      },
      {
        label: t("Geographies"),
        value: programInput.state || t("N/A"),
        type: "text",
      },
      {
        label: t("Cost to farmer"),
        value: programInput.farmer_cost
          ? `${programInput.farmer_cost} Rs. per acre`
          : t("N/A"),
        type: "text",
      },
      {
        label: t("GHG"),
        value: programInput.ghg ? `${programInput.ghg} per acre` : t("N/A"),
        type: "text",
      },
      {
        label: t("Water"),
        value: programInput.water ? `${programInput.water} per acre` : t("N/A"),
        type: "text",
      },
      {
        label: t("Program end date"),
        value: programInput.program_end_date || t("N/A"),
        type: "text",
      },
    ];
  };

  return (
    <>
      <CustomAppBar />
      <LoaderContainer loading={loading}>
        <Container
          maxWidth="xs"
          style={{
            height: "91vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {data.length > 0 ? (
            <>
              <div
                style={{
                  overflowY: "auto",
                  height: "83%",
                  textAlign: "center",
                  maxHeight: "83%",
                  marginTop: "15px",
                }}
              >
                {data.map((program) => (
                  <Box
                    key={program.id}
                    onClick={() => handleViewProgramDetails(program.id)}
                    sx={{ cursor: "pointer", mb: 2 }}
                  >
                    <CustomProgramCard
                      id={program.id}
                      data={renderProgramData(program)}
                      onEdit={() => handleEditProgram(program.id)}
                      onDelete={() => handleDeleteProgram(program.id)}
                      onView={() => handleOpenView(program.id)}
                      showMenu
                    />
                  </Box>
                ))}
              </div>
              <CustomButton hideIcon onClick={handleAddProgram}>
                {t("Create Program")}
              </CustomButton>
            </>
          ) : (
            <div
              style={{
                height: "85%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{
                  margin: "auto",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <img
                  src={Calendar}
                  alt="My Programs"
                  style={{
                    width: "127px",
                    height: "127px",
                    marginBottom: "10px",
                  }}
                />

                <CustomButton onClick={handleAddProgram}>
                  {t("Create Program")}
                </CustomButton>

                <Typography
                  variant="subtitle1"
                  sx={{ mb: 2, textAlign: "center" }}
                >
                  {t("Your Programs will be shown here once you add them.")}
                </Typography>
              </div>
            </div>
          )}
        </Container>
      </LoaderContainer>

      <ConfirmationPopup
        open={deleteConfirmationOpen}
        handleClose={() => setDeleteConfirmationOpen(false)}
        handleConfirm={handleConfirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this program?"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </>
  );
}
